import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import ChatBox from "../components/ChatBox";
import StreamViewingMenu from "../components/StreamViewingMenu";
import ChannelMusicPlayer from "../components/ChannelMusicPlayer";
import { apiUrl } from "../lib/apiBase";
import socket from "../lib/socket";
import Stream from "./Stream";
import "./Stream.css";

const TWITCH_SCRIPT = "https://player.twitch.tv/js/embed/v1.js";

function getToken() {
  return (
    localStorage.getItem("ga_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    ""
  );
}

function apiBase() {
  return (process.env.REACT_APP_API_URL || "https://streamsite-v8dd.onrender.com")
    .trim()
    .replace(/\/$/, "")
    .replace(/\/api$/i, "");
}

function normalizeChannel(value) {
  return String(value || "").trim().toLowerCase();
}

function twitchQualityValue(value) {
  if (typeof value === "string") return value.trim();
  if (value && typeof value === "object") {
    return String(
      value.group ||
        value.quality ||
        value.name ||
        value.value ||
        value.label ||
        ""
    ).trim();
  }
  return String(value || "").trim();
}

function twitchQualityHeight(value) {
  const match = String(value || "").match(/(\d{3,4})p/i);
  return match ? Number(match[1]) : 0;
}

function twitchQualityLabel(value, lowestNumeric) {
  const quality = String(value || "").trim();
  const lower = quality.toLowerCase();
  if (lower === "auto") {
    return {
      value: quality,
      label: "Auto",
      shortLabel: "Auto",
      detail: "Twitch adapts quality to your connection.",
    };
  }
  if (lower === "chunked") {
    return {
      value: quality,
      label: "Source",
      shortLabel: "Source",
      detail: "Original broadcaster quality and highest data use.",
    };
  }
  const height = twitchQualityHeight(quality);
  const isLowest = height > 0 && height === lowestNumeric;
  return {
    value: quality,
    label: isLowest ? `Data Saver • ${quality}` : quality,
    shortLabel: isLowest ? "Data Saver" : quality,
    detail: isLowest
      ? "Lowest Twitch rendition available for this broadcast."
      : "Fixed Twitch playback quality.",
  };
}

function isMuxLivePayload(data) {
  const channel = data?.channel || data || {};
  return Boolean(
    data?.player?.live ??
      data?.player?.isLive ??
      data?.isLive ??
      channel?.isLive ??
      false
  );
}

function hasMuxPlaybackPayload(data) {
  const channel = data?.channel || data || {};
  return Boolean(
    data?.player?.playbackId ||
      channel?.playbackId ||
      channel?.lastVodPlaybackId ||
      channel?.lastVodPlayback
  );
}

function loadTwitchPlayerScript() {
  if (window.Twitch?.Player) return Promise.resolve(window.Twitch);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TWITCH_SCRIPT}"]`);
    if (existing) {
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (window.Twitch?.Player) {
          window.clearInterval(timer);
          resolve(window.Twitch);
        } else if (Date.now() - started > 10000) {
          window.clearInterval(timer);
          reject(new Error("twitch_player_timeout"));
        }
      }, 100);
      return;
    }
    const script = document.createElement("script");
    script.src = TWITCH_SCRIPT;
    script.async = true;
    script.onload = () => window.Twitch?.Player
      ? resolve(window.Twitch)
      : reject(new Error("twitch_player_missing"));
    script.onerror = () => reject(new Error("twitch_player_load_failed"));
    document.head.appendChild(script);
  });
}

function MuxWebsiteMusicBridge({ channelSlug = "", enabled = false }) {
  const [host, setHost] = useState(null);

  useEffect(() => {
    if (!enabled || !channelSlug || typeof document === "undefined") {
      setHost(null);
      return undefined;
    }

    let slot = null;
    let observer = null;

    const attach = () => {
      if (slot?.isConnected) return true;
      const playerWrap = document.querySelector(".stream-page .stream-left .player-wrap");
      if (!playerWrap) return false;

      const existing = document.querySelector(
        `[data-ga-website-music-slot="${channelSlug}"]`
      );
      slot = existing || document.createElement("div");
      slot.setAttribute("data-ga-website-music-slot", channelSlug);
      slot.style.width = "100%";
      if (!existing) playerWrap.insertAdjacentElement("afterend", slot);
      setHost(slot);
      return true;
    };

    if (!attach()) {
      observer = new MutationObserver(() => {
        if (attach()) observer?.disconnect();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observer?.disconnect();
      if (slot?.isConnected) slot.remove();
    };
  }, [channelSlug, enabled]);

  if (!enabled || !host) return null;
  return createPortal(
    <ChannelMusicPlayer channelSlug={channelSlug} enabled={true} />,
    host
  );
}

export default function ConsoleAwareStream() {
  const { channel = "" } = useParams();
  const channelSlug = String(channel || "").trim().toLowerCase();
  const [source, setSource] = useState(null);
  const [sourceLoaded, setSourceLoaded] = useState(false);
  const [sourceState, setSourceState] = useState("checking");
  const [muxStateLoaded, setMuxStateLoaded] = useState(false);
  const [muxLive, setMuxLive] = useState(false);
  const [muxPlaybackAvailable, setMuxPlaybackAvailable] = useState(false);
  const [twitchQualityOptions, setTwitchQualityOptions] = useState([
    { value: "auto", label: "Auto", shortLabel: "Auto", detail: "Twitch adapts quality to your connection." },
  ]);
  const [twitchQuality, setTwitchQuality] = useState("auto");
  const [viewingStatus, setViewingStatus] = useState("");
  const playerRef = useRef(null);
  const playerHostId = useMemo(
    () => `ga-console-twitch-${Math.random().toString(36).slice(2)}`,
    []
  );

  const checkMuxLive = useCallback(async () => {
    if (!channelSlug) {
      setMuxLive(false);
      setMuxPlaybackAvailable(false);
      setMuxStateLoaded(true);
      return;
    }

    try {
      const response = await fetch(apiUrl(`/streams/${encodeURIComponent(channelSlug)}`), {
        credentials: "include",
        cache: "no-store",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return;
      setMuxLive(isMuxLivePayload(data));
      setMuxPlaybackAvailable(hasMuxPlaybackPayload(data));
      setMuxStateLoaded(true);
    } catch {
      // Fail closed: while Mux state is unknown, keep the normal GA player
      // mounted instead of risking a console/OBS source collision.
    }
  }, [channelSlug]);

  useEffect(() => {
    let cancelled = false;
    setSource(null);
    setSourceLoaded(false);
    setSourceState("checking");
    fetch(apiUrl(`/console-streaming/channel/${encodeURIComponent(channelSlug)}`), { cache: "no-store" })
      .then((response) => response.json())
      .then((data) => {
        if (!cancelled) {
          setSource(data?.source || null);
          setSourceLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setSourceLoaded(true);
      });
    return () => { cancelled = true; };
  }, [channelSlug]);

  useEffect(() => {
    let cancelled = false;
    setMuxStateLoaded(false);

    const refreshMuxLive = async () => {
      if (cancelled) return;
      await checkMuxLive();
    };

    const onLiveState = (payload = {}) => {
      if (cancelled) return;
      const eventChannel = normalizeChannel(
        payload?.channel || payload?.slug || payload?.channelId
      );
      if (!eventChannel || eventChannel !== normalizeChannel(channelSlug)) return;
      if (String(payload?.source || "").toLowerCase() !== "mux") return;
      setMuxLive(payload?.live === true);
      setMuxStateLoaded(true);
    };

    const onConnect = () => refreshMuxLive();
    const onFocus = () => refreshMuxLive();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshMuxLive();
    };

    refreshMuxLive();
    socket.on("stream:live-state", onLiveState);
    socket.on("connect", onConnect);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (!socket.connected) socket.connect();

    return () => {
      cancelled = true;
      socket.off("stream:live-state", onLiveState);
      socket.off("connect", onConnect);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [channelSlug, checkMuxLive]);

  useEffect(() => {
    if (!sourceLoaded || !muxStateLoaded || muxLive || !source?.enabled || !source?.twitchChannel) {
      if (muxLive) {
        setSourceState("checking");
        const host = document.getElementById(playerHostId);
        if (host) host.innerHTML = "";
        playerRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    let fallbackTimer = null;

    async function boot() {
      try {
        const Twitch = await loadTwitchPlayerScript();
        if (cancelled || muxLive) return;
        const parents = Array.from(new Set([
          window.location.hostname,
          "gastreaming.live",
          "www.gastreaming.live",
          "stream-site-rho.vercel.app",
        ].filter(Boolean)));
        const player = new Twitch.Player(playerHostId, {
          channel: source.twitchChannel,
          width: "100%",
          height: "100%",
          autoplay: true,
          muted: true,
          parent: parents,
        });
        playerRef.current = player;

        const refreshViewingOptions = () => {
          if (cancelled || playerRef.current !== player) return;
          try {
            const rawQualities = Array.from(
              new Set((player.getQualities?.() || []).map(twitchQualityValue).filter(Boolean))
            );
            const numeric = rawQualities
              .map(twitchQualityHeight)
              .filter((value) => value > 0)
              .sort((a, b) => a - b);
            const lowestNumeric = numeric[0] || 0;
            const ordered = rawQualities.sort((a, b) => {
              const al = a.toLowerCase();
              const bl = b.toLowerCase();
              if (al === "auto") return -1;
              if (bl === "auto") return 1;
              if (al === "chunked") return 1;
              if (bl === "chunked") return -1;
              return twitchQualityHeight(a) - twitchQualityHeight(b);
            });
            if (ordered.length) {
              setTwitchQualityOptions(
                ordered.map((quality) => twitchQualityLabel(quality, lowestNumeric))
              );
            }

            let preferred = "";
            try {
              preferred = String(localStorage.getItem("ga_twitch_quality") || "").trim();
            } catch {
              preferred = "";
            }
            if (preferred && ordered.includes(preferred)) {
              try {
                player.setQuality(preferred);
              } catch {
                // Fall back to Twitch's current quality.
              }
            }

            const current = twitchQualityValue(
              preferred && ordered.includes(preferred) ? preferred : player.getQuality?.()
            ) || "auto";
            setTwitchQuality(current);
          } catch {
            // Twitch can report qualities after PLAYING instead of READY.
          }

          const host = document.getElementById(playerHostId);
          const iframe = host?.querySelector?.("iframe");
          if (iframe) {
            const allow = String(iframe.getAttribute("allow") || "");
            const permissions = new Set(
              allow
                .split(";")
                .map((item) => item.trim())
                .filter(Boolean)
            );
            permissions.add("autoplay");
            permissions.add("fullscreen");
            permissions.add("picture-in-picture");
            iframe.setAttribute("allow", Array.from(permissions).join("; "));
            iframe.setAttribute("allowfullscreen", "true");
          }
        };

        player.addEventListener(Twitch.Player.READY, refreshViewingOptions);
        player.addEventListener(Twitch.Player.ONLINE, () => !cancelled && setSourceState("online"));
        player.addEventListener(Twitch.Player.PLAYING, () => {
          if (cancelled) return;
          setSourceState("online");
          refreshViewingOptions();
        });
        player.addEventListener(Twitch.Player.OFFLINE, () => !cancelled && setSourceState("offline"));
        player.addEventListener(Twitch.Player.ENDED, () => !cancelled && setSourceState("offline"));
        fallbackTimer = window.setTimeout(() => {
          if (!cancelled) setSourceState((current) => current === "checking" ? "offline" : current);
        }, 12000);
      } catch (error) {
        console.warn("[console-direct] Twitch player failed", error?.message || error);
        if (!cancelled) setSourceState("offline");
      }
    }

    boot();
    return () => {
      cancelled = true;
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      playerRef.current = null;
      const host = document.getElementById(playerHostId);
      if (host) host.innerHTML = "";
    };
  }, [muxLive, muxStateLoaded, playerHostId, source?.enabled, source?.twitchChannel, sourceLoaded]);

  useEffect(() => {
    if (muxLive || sourceState !== "online") return undefined;
    const timer = window.setInterval(async () => {
      const token = getToken();
      if (!token || document.visibilityState !== "visible") return;
      try {
        const response = await fetch(`${apiBase()}/api/watch-time/heartbeat`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            seconds: 30,
            playing: true,
            visible: true,
            path: window.location.pathname,
            source: "console-direct-twitch-bridge",
          }),
        });
        const data = await response.json().catch(() => null);
        if (response.ok && data?.user) {
          const current = JSON.parse(localStorage.getItem("user") || "{}");
          localStorage.setItem("user", JSON.stringify({ ...current, ...data.user }));
          window.dispatchEvent(new Event("auth:changed"));
        }
      } catch {}
    }, 30000);
    return () => window.clearInterval(timer);
  }, [muxLive, sourceState]);

  const changeTwitchQuality = useCallback((nextQuality) => {
    const player = playerRef.current;
    if (!player || !nextQuality) return;
    try {
      player.setQuality(nextQuality);
      setTwitchQuality(nextQuality);
      try {
        localStorage.setItem("ga_twitch_quality", nextQuality);
      } catch {
        // ignore
      }
      setViewingStatus(
        String(nextQuality).toLowerCase() === "auto"
          ? "Automatic Twitch quality is on."
          : `Twitch quality set to ${nextQuality}.`
      );
    } catch (error) {
      console.warn("[console-direct] Twitch quality change failed", error?.message || error);
      setViewingStatus("That Twitch quality is not available right now.");
    }
  }, []);

  const requestTwitchPictureInPicture = useCallback(async () => {
    const host = document.getElementById(playerHostId);
    const iframe = host?.querySelector?.("iframe");
    if (iframe) {
      const allow = String(iframe.getAttribute("allow") || "");
      const permissions = new Set(
        allow
          .split(";")
          .map((item) => item.trim())
          .filter(Boolean)
      );
      permissions.add("picture-in-picture");
      permissions.add("fullscreen");
      iframe.setAttribute("allow", Array.from(permissions).join("; "));
    }

    setViewingStatus(
      "Twitch controls this console video. Picture-in-Picture is enabled for the embed; use the Twitch player’s PiP/pop-out control when your phone/browser exposes it."
    );
    return true;
  }, [playerHostId]);

  const renderMuxStream = () => (
    <>
      <Stream />
      <MuxWebsiteMusicBridge
        channelSlug={channelSlug}
        enabled={source?.websiteMusicEnabled === true}
      />
    </>
  );

  // OBS/Mux is always authoritative. Console playback is only a fallback source.
  if (!sourceLoaded || !muxStateLoaded || muxLive) return renderMuxStream();
  if (!source?.enabled || !source?.twitchChannel) return renderMuxStream();
  if (sourceState === "offline" && muxPlaybackAvailable) return renderMuxStream();

  const platformLabel = source?.platform === "xbox" ? "XBOX DIRECT" : "PS5 DIRECT";
  const displayName = source?.channelName || channelSlug;
  const consoleStatus = sourceState === "offline"
    ? "CONSOLE CONNECTED • WAITING FOR BROADCAST"
    : "CHECKING CONSOLE BROADCAST…";

  return (
    <div className="stream-page">
      <style>{`
        .ga-console-direct-wrap{position:relative;width:100%;aspect-ratio:16/9;background:#000;overflow:hidden;border-radius:12px}
        .ga-console-direct-player{position:absolute;inset:0;width:100%;height:100%;background:#000}
        .ga-console-direct-player>div,.ga-console-direct-player iframe{width:100%!important;height:100%!important;display:block!important}
        .ga-console-direct-badge{display:inline-flex;align-items:center;gap:7px;padding:6px 10px;border-radius:999px;border:1px solid rgba(255,75,88,.35);background:rgba(177,22,35,.23);color:#ffb3ba;font-size:10px;font-weight:1000;letter-spacing:.08em}
        .ga-console-direct-dot{width:8px;height:8px;border-radius:50%;background:#ff3447;box-shadow:0 0 14px rgba(255,52,71,.75)}
        .ga-console-checking{display:inline-flex;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.08);color:rgba(255,255,255,.72);font-size:10px;font-weight:900;letter-spacing:.06em}
      `}</style>
      <div className="stream-left">
        <div className="stream-topbar">
          <div className="stream-topbar__left">
            <Link to="/streams" className="stream-backlink">← Channels</Link>
            <div className="stream-topbar__meta">
              <div className="stream-topbar__title">{displayName}{sourceState === "online" ? " • LIVE" : ""}</div>
              <div style={{ marginTop: 7 }}>
                {sourceState === "online" ? (
                  <span className="ga-console-direct-badge"><span className="ga-console-direct-dot" />{platformLabel}</span>
                ) : (
                  <span className="ga-console-checking">{consoleStatus}</span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="player-wrap">
          <div className="ga-console-direct-wrap">
            <div id={playerHostId} className="ga-console-direct-player" />
            <StreamViewingMenu
              qualityOptions={twitchQualityOptions}
              qualityValue={twitchQuality}
              onQualityChange={changeTwitchQuality}
              onPictureInPicture={requestTwitchPictureInPicture}
              pictureInPictureAvailable={true}
              sourceLabel="Console direct • Twitch"
              statusText={
                viewingStatus ||
                "Choose the lowest available Twitch rendition for slow data. PiP permission is enabled for the Twitch embed when the device/browser exposes it."
              }
            />
          </div>
        </div>
        <ChannelMusicPlayer
          channelSlug={channelSlug}
          enabled={source?.websiteMusicEnabled === true}
        />
      </div>
      <div className="stream-right">
        <ChatBox room={`live:${channelSlug}`} channelId={channelSlug} />
      </div>
    </div>
  );
}
